import { Box, Center, Flex, Skeleton, Text, chakra } from "@chakra-ui/react";
import { useContext } from "react";
import { FormattedMessage } from "react-intl";

import { LeftArrowIcon, RightArrowIcon } from "../../../icons";
import { RanaButton } from "../../components/rana-button";
import { RanaSelect } from "../../rana-select";
import { useTablePagination } from "../hooks/use-table-pagination";
import { TableContext } from "../rana-table";

export interface TablePaginationProps {
  limit: number;
  setPageSize?: (size: number) => void;
  setPage: (page: number) => void;
  page: number;
  numPages: number;
  pagesNextToCurrent?: number;
}

function PaginationButton({
  page,
  isCurrent,
  setPage,
}: {
  page: number;
  isCurrent: boolean;
  setPage: () => void;
}) {
  return (
    <Center
      {...(isCurrent
        ? {
            as: RanaButton,
            variant: "secondary",
            p: 0,
            cursor: "default",
          }
        : { cursor: "pointer", onClick: setPage })}
      h="button"
      w="button"
      my={1}
      alignItems="center"
      rounded="base"
    >
      {page}
    </Center>
  );
}

export function TablePagination({
  limit,
  setPageSize,
  setPage,
  page,
  numPages,
  pagesNextToCurrent = 2,
}: TablePaginationProps) {
  const { isSkeleton } = useContext(TableContext);

  const {
    paginationRef,
    pageTextRef,
    pageSizeRef,
    wrapPageSize,
    hidePageText,
    showLeftDots,
    showRightDots,
    middlePart,
    canClickLeft,
    canClickRight,
  } = useTablePagination({ page, numPages, pagesNextToCurrent });

  return (
    <Flex as={isSkeleton ? Skeleton : undefined} mt={4}>
      <Center w="full" position="relative" flexDirection="column" gap={6}>
        <Flex
          ref={paginationRef}
          alignItems="center"
          justifyContent="center"
          gap={1}
          borderWidth="base"
          borderColor="brand.border"
          rounded="base"
          pr={4}
          bg="brand.grey.200"
        >
          <Box p={4} cursor={canClickLeft ? "pointer" : undefined}>
            <LeftArrowIcon
              onClick={() => canClickLeft && setPage(page - 1)}
              color={canClickLeft ? "brand.deep_sea" : "brand.grey.500"}
            />
          </Box>

          <PaginationButton page={1} isCurrent={page === 1} setPage={() => setPage(1)} />
          {showLeftDots ? <chakra.span color="brand.grey.500">{"\u2026"}</chakra.span> : null}
          {middlePart.map((i) => (
            <PaginationButton key={i} page={i} isCurrent={page === i} setPage={() => setPage(i)} />
          ))}
          {showRightDots ? <chakra.span color="brand.grey.500">{"\u2026"}</chakra.span> : null}
          {numPages > 1 ? (
            <PaginationButton
              page={numPages}
              isCurrent={page === numPages}
              setPage={() => setPage(numPages)}
            />
          ) : null}

          <Box pl={4} cursor={canClickRight ? "pointer" : undefined}>
            <RightArrowIcon
              onClick={() => canClickRight && setPage(page + 1)}
              color={canClickRight ? "brand.deep_sea" : "brand.grey.500"}
            />
          </Box>
        </Flex>
        {setPageSize ? (
          <Flex
            position={!wrapPageSize ? "absolute" : undefined}
            color="brand.deep_sea"
            alignSelf="flex-end"
            right={0}
            gap={3}
            alignItems="center"
            pointerEvents={hidePageText ? "none" : undefined}
          >
            <Text ref={pageTextRef} opacity={hidePageText ? 0 : undefined}>
              <FormattedMessage id="pagination.items" defaultMessage="Items per page:" />
              &nbsp;
            </Text>

            <Box pointerEvents="all" ref={pageSizeRef}>
              <RanaSelect
                options={[
                  { value: 5, label: 5 },
                  { value: 10, label: 10 },
                  { value: 20, label: 20 },
                  { value: 50, label: 50 },
                  { value: 100, label: 100 },
                ]}
                onChange={(limit) => setPageSize(Number(limit))}
                value={limit}
              />
            </Box>
          </Flex>
        ) : null}
      </Center>
    </Flex>
  );
}
